import { useCallback, useRef, useState, useEffect } from "react";
import Scrollbars from "react-custom-scrollbars-2";

import classNames from "classnames";
import {
    Box,
    boxesIntersect,
    useSelectionContainer,
} from "../../../lib/dragToSelect";
import { useKeyboardCommands } from "../../../hooks/keyboard/useKeyboardCommands";
import { copyTextToClipboard } from "../../../lib/clipboardUtils";
import styles from "./EditableTable.module.scss";
import {
    CellGrid,
    EDITABLE_GRID_COLS,
    EditableCellItem,
    NON_EDITABLE_GRID_COLS,
    calculateSelectedArea,
    convertLinesToCells,
    doesSelectedAreaContainOnlyOneCell,
    getCellByPosition,
    getItemBoundingClientRect,
    isCellInSelectedArea,
    isDraggingOrderCell,
    moveArrayChunk,
} from "./EditableTableUtils";
import { useEditableTableContext } from "./EditableTableContext";

interface Props<T> {
    tableRef: React.RefObject<HTMLDivElement>;
    scrollbarsRef: React.RefObject<Scrollbars>;
    setFieldValue: (
        field: string,
        value: any,
        shouldValidate?: boolean | undefined,
    ) => void;
    isEditable?: boolean;
    // NOTE: for now we support only one type of search options
    // (assuming all searchable columns have the same type)

    children: React.ReactNode;

    onCut: (e: any) => void;
    onPaste: (e: any) => void;

    formValueName: string;

    lineToInsert: number | null;
    setLineToInsert: React.Dispatch<React.SetStateAction<number | null>>;

    columnIndexToName: Record<number, keyof T>;
}

const DRAGGING_THRESHOLD = 2;

export const EditableTableBodyCore = <T,>({
    tableRef,
    scrollbarsRef,
    setFieldValue,
    isEditable = true,
    children,
    onCut,
    onPaste,
    formValueName,
    lineToInsert,
    setLineToInsert,
    columnIndexToName,
}: Props<T>) => {
    const selectableItemsRef = useRef<CellGrid<EditableCellItem>>([]);
    const {
        selectedArea,
        setSelectedArea,
        combineWithExistingSelectedArea,
        targetLines,
        bodyRef,
        setIsInBuffer,
    } = useEditableTableContext<T>();
    const linesRef = useRef(targetLines);
    const dragState = useRef<"dragging" | "selecting">("selecting");
    const [draggingAreaBox, setDraggingAreaBox] = useState<Box | null>(null);
    const draggingAreaOffset = useRef<number | null>(null);

    const calculateDraggingArea = useCallback(
        (box: Box, event: MouseEvent) => {
            let boxTop =
                box.top +
                box.height -
                getItemBoundingClientRect(tableRef.current!).top;
            if (box.top === event.y) {
                boxTop =
                    box.top - getItemBoundingClientRect(tableRef.current!).top;
            }
            const draggingState = {
                top: boxTop - (draggingAreaOffset.current ?? 0),
                left: event.x === box.left ? box.left : box.left + box.width,
                height: selectedArea
                    ? (selectedArea?.bottomRightCellPosition.rowIndex -
                          selectedArea?.topLeftCellPosition.rowIndex +
                          1) *
                      64
                    : 0,
                width: 0,
            };
            return draggingState;
        },
        [selectedArea, tableRef],
    );

    const recalculateSelectableItems = useCallback(() => {
        if (tableRef.current) {
            selectableItemsRef.current = [];
            Array.from(tableRef.current.children).forEach((line, rowIndex) => {
                const cells = Array.from(line.children).filter((child) =>
                    child.classList.contains(styles.editableTableCell),
                );
                cells.forEach((cell, columnIndex) => {
                    const itemToStore: EditableCellItem = {
                        topToParent: (cell as HTMLElement).offsetTop,
                        item: cell as HTMLElement,
                        cell: { rowIndex, columnIndex },
                    };

                    selectableItemsRef.current[rowIndex] ??= [];

                    selectableItemsRef.current[rowIndex]![columnIndex] =
                        itemToStore;
                });
            });
        }
    }, [tableRef]);

    useEffect(() => {
        linesRef.current = targetLines;
        recalculateSelectableItems();
    }, [targetLines, recalculateSelectableItems]);

    const handleDraggingState = (box: Box, event: MouseEvent) => {
        const draggingState = calculateDraggingArea(box, event);
        setDraggingAreaBox(draggingState);

        let lineToInsertNew: number | null = null;
        for (const row of selectableItemsRef.current) {
            for (const cell of row!) {
                if (
                    boxesIntersect(
                        {
                            ...draggingState,
                            top:
                                event.y === box.top
                                    ? box.top
                                    : box.top + box.height,
                            height: 0,
                            width: 0,
                        },
                        getItemBoundingClientRect(cell.item),
                    )
                ) {
                    lineToInsertNew = cell.cell.rowIndex;
                }
            }
        }
        if (
            selectedArea &&
            lineToInsertNew !== null &&
            lineToInsertNew > selectedArea.topLeftCellPosition.rowIndex &&
            lineToInsertNew <= selectedArea.bottomRightCellPosition.rowIndex
        ) {
            lineToInsertNew = selectedArea.topLeftCellPosition.rowIndex;
        }
        if (lineToInsert !== lineToInsertNew) {
            setLineToInsert(lineToInsertNew);
        }
    };

    const handleSelectionState = (box: Box, event: MouseEvent) => {
        dragState.current = "selecting";
        if (draggingAreaBox !== null) {
            setDraggingAreaBox(null);
        }
        const { topLeftCellPosition, bottomRightCellPosition } =
            calculateSelectedArea(box, selectableItemsRef.current);

        if (event.shiftKey) {
            combineWithExistingSelectedArea(
                topLeftCellPosition,
                bottomRightCellPosition,
            );
        } else {
            setSelectedArea(topLeftCellPosition, bottomRightCellPosition);
        }
    };

    const handleTransitToDragging = (box: Box, event: MouseEvent) => {
        const conditionToSwitchState =
            isEditable &&
            selectedArea !== null &&
            selectedArea.bottomRightCellPosition.columnIndex !==
                selectedArea.topLeftCellPosition.columnIndex &&
            !(
                selectedArea?.topLeftCellPosition.rowIndex === 0 &&
                selectedArea?.bottomRightCellPosition.rowIndex ===
                    linesRef.current.length - 1
            ) &&
            box.width < DRAGGING_THRESHOLD &&
            box.height < DRAGGING_THRESHOLD;

        if (!conditionToSwitchState) {
            return false;
        }
        if (
            !isDraggingOrderCell(box, selectedArea, selectableItemsRef.current)
        ) {
            return false;
        }
        dragState.current = "dragging";
        document.body.classList.add(styles.bodyDragging);
        const lineToInsertNew = selectedArea.topLeftCellPosition.rowIndex;
        if (lineToInsert !== lineToInsertNew) {
            setLineToInsert(lineToInsertNew);
        }

        if (draggingAreaOffset.current === null) {
            const topCell = getCellByPosition(
                selectedArea.topLeftCellPosition,
                selectableItemsRef.current,
            );

            draggingAreaOffset.current =
                box.top - getItemBoundingClientRect(topCell!.item).top;
        }
        const draggingState = calculateDraggingArea(box, event);
        setDraggingAreaBox(draggingState);

        return true;
    };

    const onDragEnd = () => {
        if (dragState.current !== "dragging") {
            return;
        }

        dragState.current = "selecting";
        if (selectedArea === null) {
            setDraggingAreaBox(null);
            setLineToInsert(null);
            return;
        }

        const topLeftCell = getCellByPosition(
            selectedArea.topLeftCellPosition,
            selectableItemsRef.current,
        );
        const bottomRightCell = getCellByPosition(
            selectedArea.bottomRightCellPosition,
            selectableItemsRef.current,
        );

        if (!topLeftCell || !bottomRightCell) {
            return;
        }
        if (lineToInsert === null) {
            return;
        }
        const lineFrom = topLeftCell.cell.rowIndex;
        const lineTo = bottomRightCell.cell.rowIndex;

        const newLines = moveArrayChunk({
            array: linesRef.current,
            startIndex: lineFrom,
            chunkSize: lineTo - lineFrom + 1,
            newIndex: lineToInsert,
        });

        setFieldValue(formValueName, newLines);
        setSelectedArea(null, null);
        setDraggingAreaBox(null);
        setLineToInsert(null);
        draggingAreaOffset.current = null;
        document.body.classList.remove(styles.bodyDragging);
    };

    const { DragSelection } = useSelectionContainer({
        eventsElement: scrollbarsRef.current?.container,
        onSelectionCancel: onDragEnd,
        onSelectionEnd: onDragEnd,
        onSelectionChange: (box, event) => {
            if (dragState.current === "dragging") {
                handleDraggingState(box, event);
                return;
            }

            const isTransitToDragging = handleTransitToDragging(box, event);
            if (isTransitToDragging) {
                return;
            }

            handleSelectionState(box, event);
        },
        selectionProps: {
            style: {
                opacity: 0,
            },
        },
    });

    const onBackspace = useCallback(
        (e: any) => {
            if (selectedArea === null) {
                return;
            }
            if (
                doesSelectedAreaContainOnlyOneCell(selectedArea) &&
                document.activeElement instanceof HTMLInputElement
            ) {
                return;
            }
            e.preventDefault();
            const newLines = [...linesRef.current];

            selectableItemsRef.current.forEach((line) => {
                line!.forEach((cell) => {
                    if (!isCellInSelectedArea(cell.cell, selectedArea)) {
                        return;
                    }
                    const columnName = columnIndexToName[cell.cell.columnIndex];

                    const lineIndex = cell.cell.rowIndex;
                    const newLine: T = {
                        ...newLines[lineIndex],
                    };
                    newLines[lineIndex] = newLine;
                    (newLine as any)[columnName] = "";
                });
            });
            setFieldValue(formValueName, newLines);
        },
        [
            selectedArea,
            columnIndexToName,
            setFieldValue,
            selectableItemsRef,
            formValueName,
        ],
    );

    const onCopy = useCallback(
        (e: any) => {
            if (!selectedArea) {
                return;
            }
            e.preventDefault();

            const effectiveToColumn =
                selectedArea.bottomRightCellPosition.columnIndex !== 0
                    ? selectedArea.bottomRightCellPosition.columnIndex
                    : 1;
            const cells = convertLinesToCells({
                lines: linesRef.current,
                cellsIndexesToColumnsMapping: columnIndexToName,
                fromLine: selectedArea.topLeftCellPosition.rowIndex,
                toLine: selectedArea.bottomRightCellPosition.rowIndex,
                // NOTE: we don't want to copy order cells
                fromColumn:
                    selectedArea.topLeftCellPosition.columnIndex !== 0
                        ? selectedArea.topLeftCellPosition.columnIndex
                        : 1,
                toColumn: effectiveToColumn,
            });
            copyTextToClipboard(
                cells.map((row) => row.join("	")).join("\n"),
            ).then((result) => {
                if (result) {
                    setIsInBuffer(true);
                }
            });
        },
        [selectedArea, setIsInBuffer, columnIndexToName, linesRef],
    );

    const editableCommands = [
        {
            key: "Backspace",
            requiresCtrlOrMeta: false,
            callback: onBackspace,
            preventDefault: false,
        },
        {
            key: "x",
            callback: onCut,
            preventDefault: false,
        },
        {
            key: "v",
            callback: onPaste,
            preventDefault: false,
        },
    ];

    useKeyboardCommands({
        commands: [
            {
                key: "c",
                callback: onCopy,
                preventDefault: false,
            },
            ...(isEditable ? editableCommands : []),
        ],
    });

    return (
        <div
            ref={bodyRef}
            className={classNames(
                styles.container,
                draggingAreaBox ? styles.bodyDragging : "",
            )}
            style={
                {
                    "--grid-template-columns": isEditable
                        ? EDITABLE_GRID_COLS
                        : NON_EDITABLE_GRID_COLS,
                } as any
            }
        >
            <DragSelection />
            {children}
        </div>
    );
};
